var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Accordion, Button, Dialog, Input, Spinner, Toast, Toaster, ToastTitle, useId, useToastController, } from "@fluentui/react-components";
import SadFaceEmoji from "/public/emojis/SadFace.svg";
import { AddRegular, SearchRegular, SettingsRegular, } from "@fluentui/react-icons";
import PolicySource from "./PolicySource";
import AddPolicySourceModal from "./modals/AddPolicySourceModal";
import { PoliciesService } from "./PoliciesService";
import SettingsModal from "./modals/SettingsModal";
const Container = styled.div.withConfig({ displayName: "Container", componentId: "-2e5118" }) `
  margin-inline-end: 3rem;

  > h1 {
    display: flex;
    margin-bottom: 0.5rem;
  }

  > h4 {
    font-weight: 400;
    margin-bottom: 2.5rem;
  }
`;
const GroupContainer = styled.div.withConfig({ displayName: "GroupContainer", componentId: "-2xac5f" }) ``;
const FiltersContainer = styled.div.withConfig({ displayName: "FiltersContainer", componentId: "-7b72t7" }) `
  display: flex;
  justify-content: space-between;
`;
const FiltersGroupLeftContainer = styled.div.withConfig({ displayName: "FiltersGroupLeftContainer", componentId: "-urzm3u" }) `
  display: flex;
`;
const SearchInput = styled(Input).withConfig({ displayName: "SearchInput", componentId: "-1y5u51y" }) `
  min-width: 240px;
  width: 357px;
  margin-right: 32px;
`;
const NewPoliciesButton = styled(Button).withConfig({ displayName: "NewPoliciesButton", componentId: "-19pt3n0" }) `
  width: 160px;
`;
const BodyContainer = styled.div.withConfig({ displayName: "BodyContainer", componentId: "-uxi80m" }) `
  display: flex;
  flex-direction: column;
  margin-top: 28px;
`;
const EmptyStateContainer = styled.div.withConfig({ displayName: "EmptyStateContainer", componentId: "-136mwv6" }) `
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
`;
const EmptyStateIcon = styled.img.withConfig({ displayName: "EmptyStateIcon", componentId: "-xg66xj" }) `
  width: 65px;
  height: 65px;
`;
const EmptyStateBody = styled.div.withConfig({ displayName: "EmptyStateBody", componentId: "-1j1zhof" }) `
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const EmptyStateMessage = styled.div.withConfig({ displayName: "EmptyStateMessage", componentId: "-13epv55" }) `
  text-align: center;
`;
const SubTitle = styled.div.withConfig({ displayName: "SubTitle", componentId: "-l9of30" }) `
  display: flex;
  justify-content: space-between;
  align-items: center;
  > h4 {
    font-weight: 400;
    margin-bottom: 2.5rem;
  }
`;
const PoliciesLayout = () => {
    const { GetSources, SetPolicyItemAsMandatory, EnableAssistant } = PoliciesService();
    const [sources, setSources] = useState([]);
    const [filteredPolicies, setFilteredPolicies] = useState();
    const [searchInput, setSearchInput] = useState("");
    const [showAddPolicySourceModal, setShowAddPolicySourceModal] = useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const toasterId = useId("toaster");
    const { dispatchToast } = useToastController(toasterId);
    const emptyStateMessage = "Click the button below to start creating policies\nand manage how others can access and read your content.";
    const getInfo = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoading(true);
        try {
            const sources = yield GetSources();
            setSources(sources);
            setFilteredPolicies(sources);
        }
        catch (e) {
            console.error(e);
            setIsLoading(false);
        }
        setIsLoading(false);
    });
    useEffect(() => {
        getInfo();
    }, []);
    useEffect(() => {
        const filteredPolicies = sources.length > 0
            ? sources === null || sources === void 0 ? void 0 : sources.map((source) => {
                const filteredItems = source.items.filter((item) => item.title
                    .toLocaleLowerCase()
                    .includes(searchInput.toLocaleLowerCase()));
                return Object.assign(Object.assign({}, source), { items: filteredItems });
            })
            : [];
        setFilteredPolicies(filteredPolicies);
    }, [sources, searchInput]);
    const notify = (message) => {
        return dispatchToast(_jsx(Toast, { children: _jsx(ToastTitle, { children: message }) }), { intent: "success" });
    };
    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
        setFilteredPolicies(filteredPolicies);
    };
    const onMandatoryPolicyItemChange = (policyItemId) => __awaiter(void 0, void 0, void 0, function* () {
        const source = sources.find((source) => source.items.find((item) => item.id === policyItemId));
        const item = source.items.find((item) => item.id === policyItemId);
        yield SetPolicyItemAsMandatory(source.id, item.id, !item.mandatory);
        notify("Policy mandatory setting changed successfully");
        getInfo();
    });
    const onEnabledAssistantChange = (policyItemId) => __awaiter(void 0, void 0, void 0, function* () {
        const source = sources.find((source) => source.items.find((item) => item.id === policyItemId));
        const item = source.items.find((item) => item.id === policyItemId);
        yield EnableAssistant(source.id, item.id, !item.enabledAssistant);
        notify("AI assistant setting changed successfully");
        getInfo();
    });
    const handleSettingsModalOpen = () => {
        setShowSettingsModal(true);
    };
    const handleSettingsModalClose = (message) => {
        if (message) {
            notify(message);
        }
        setShowSettingsModal(false);
    };
    const handleAddPolicySourceModalOpen = () => {
        setShowAddPolicySourceModal(true);
    };
    const handleAddPolicySourceModalClose = () => __awaiter(void 0, void 0, void 0, function* () {
        setShowAddPolicySourceModal(false);
    });
    return (_jsxs(Container, { children: [_jsx("h1", { children: "Policies" }), _jsxs(SubTitle, { children: [_jsx("h4", { children: "Add and manage policies and define which are mandatory." }), _jsx(Button, Object.assign({ icon: _jsx(SettingsRegular, {}), onClick: handleSettingsModalOpen }, { children: "Settings" }))] }), isLoading ? (_jsx(Spinner, { size: "small", label: "We're getting everything ready for you..." })) : (sources === null || sources === void 0 ? void 0 : sources.length) == 0 ? (_jsxs(EmptyStateContainer, { children: [_jsx(EmptyStateIcon, { src: SadFaceEmoji, alt: ":/" }), _jsxs(EmptyStateBody, { children: [_jsx("h3", { children: "You haven\u2019t added any policies yet." }), _jsx(EmptyStateMessage, { children: emptyStateMessage.split("\n").map((line, index) => (_jsxs(React.Fragment, { children: [line, _jsx("br", {})] }, index))) }), _jsx("br", {}), _jsx(NewPoliciesButton, Object.assign({ appearance: "primary", onClick: handleAddPolicySourceModalOpen }, { children: "Add policies" }))] })] })) : (_jsxs(GroupContainer, { children: [_jsx(FiltersContainer, { children: _jsxs(FiltersGroupLeftContainer, { children: [_jsx(SearchInput, { contentBefore: _jsx(SearchRegular, {}), placeholder: "Search for policies", value: searchInput, onChange: handleSearchInputChange }), _jsx(NewPoliciesButton, Object.assign({ appearance: "primary", icon: _jsx(AddRegular, {}), onClick: handleAddPolicySourceModalOpen }, { children: "Add new policies" }))] }) }), _jsx(BodyContainer, { children: _jsx(Accordion, Object.assign({ collapsible: true, defaultOpenItems: filteredPolicies.map((policy) => policy.id), multiple: true }, { children: filteredPolicies.map((policySource) => (_jsx(PolicySource, { policySource: policySource, onMandatoryPolicyItemChange: onMandatoryPolicyItemChange, onEnabledAssistantChange: onEnabledAssistantChange, onSave: getInfo }, policySource.id))) })) })] })), _jsx(Dialog, Object.assign({ open: showAddPolicySourceModal, onOpenChange: handleAddPolicySourceModalClose }, { children: _jsx(AddPolicySourceModal, { onClose: handleAddPolicySourceModalClose, onSave: getInfo }) })), _jsx(Dialog, Object.assign({ open: showSettingsModal, onOpenChange: handleSettingsModalClose }, { children: _jsx(SettingsModal, { onClose: handleSettingsModalClose }) })), _jsx(Toaster, { toasterId: toasterId, position: "top-end", mountNode: undefined })] }));
};
export default PoliciesLayout;
